/**
 * Proof of Work: challenge generation, solving (via Web Worker), and verification.
 * 22-bit SHA-256 by default. Main thread remains responsive during solving.
 */

import { POW_DIFFICULTY } from '../constants'
import type { PowSolution } from '../types'
import type { PowWorkerResponse, PowWorkerError } from './pow-worker'

// ============================================================
// Challenge Generation
// ============================================================

/**
 * Generate a random 32-byte challenge.
 */
export function generateChallenge(): Uint8Array {
  const challenge = new Uint8Array(32)
  crypto.getRandomValues(challenge)
  return challenge
}

// ============================================================
// Solving (Web Worker)
// ============================================================

/**
 * Solve a PoW challenge using a dedicated Web Worker.
 * Returns a nonce whose SHA-256(challenge || nonce) has `difficulty` leading zero bits.
 * Default difficulty is POW_DIFFICULTY (22 bits).
 */
export async function solveChallenge(
  challenge: Uint8Array,
  difficulty: number = POW_DIFFICULTY,
): Promise<PowSolution> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(
      new URL('./pow-worker.ts', import.meta.url),
      { type: 'module' },
    )

    worker.addEventListener('message', (event: MessageEvent) => {
      const data = event.data as PowWorkerResponse | PowWorkerError
      worker.terminate()

      if ('error' in data) {
        reject(new Error(data.error))
      } else {
        resolve({
          nonce: new Uint8Array(data.nonce),
          hash: new Uint8Array(data.hash),
        })
      }
    })

    worker.addEventListener('error', (event) => {
      worker.terminate()
      reject(new Error(`PoW worker error: ${event.message}`))
    })

    // Transfer the challenge buffer to the worker
    const challengeCopy = new Uint8Array(challenge)
    worker.postMessage(
      { challenge: challengeCopy.buffer, difficulty },
      [challengeCopy.buffer],
    )
  })
}

// ============================================================
// Verification (Main Thread)
// ============================================================

/**
 * Check if a hash has the required number of leading zero bits.
 */
function hasLeadingZeroBits(hash: Uint8Array, bits: number): boolean {
  const fullBytes = Math.floor(bits / 8)
  const remainingBits = bits % 8

  for (let i = 0; i < fullBytes; i++) {
    if (hash[i] !== 0) return false
  }

  if (remainingBits > 0 && fullBytes < hash.length) {
    const mask = 0xff << (8 - remainingBits)
    if ((hash[fullBytes]! & mask) !== 0) return false
  }

  return true
}

/**
 * Concatenate challenge and nonce to form the preimage.
 * Returns an ArrayBuffer for direct use with crypto.subtle.digest.
 */
function buildPreimage(challenge: Uint8Array, nonce: Uint8Array): ArrayBuffer {
  const buf = new ArrayBuffer(challenge.length + nonce.length)
  const view = new Uint8Array(buf)
  view.set(challenge, 0)
  view.set(nonce, challenge.length)
  return buf
}

/**
 * Verify a PoW solution on the main thread.
 * Returns true if SHA-256(challenge || nonce) has `difficulty` leading zero bits.
 */
export async function verifyChallenge(
  challenge: Uint8Array,
  solution: PowSolution,
  difficulty: number = POW_DIFFICULTY,
): Promise<boolean> {
  const preimage = buildPreimage(challenge, solution.nonce)
  const hashBuffer = await crypto.subtle.digest('SHA-256', preimage)
  const hash = new Uint8Array(hashBuffer)

  // Verify the hash matches what the solver claims
  if (solution.hash.length !== hash.length) return false
  for (let i = 0; i < hash.length; i++) {
    if (hash[i] !== solution.hash[i]) return false
  }

  return hasLeadingZeroBits(hash, difficulty)
}

// ============================================================
// Base64 helpers for signaling messages
// ============================================================

export function solutionToSignaling(
  challenge: Uint8Array,
  solution: PowSolution,
): { challenge: string; nonce: string; hash: string } {
  return {
    challenge: uint8ArrayToBase64Url(challenge),
    nonce: uint8ArrayToBase64Url(solution.nonce),
    hash: uint8ArrayToBase64Url(solution.hash),
  }
}

export function signalingToSolution(msg: {
  nonce: string
  hash: string
}): PowSolution {
  return {
    nonce: base64UrlToUint8Array(msg.nonce),
    hash: base64UrlToUint8Array(msg.hash),
  }
}

function uint8ArrayToBase64Url(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!)
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function base64UrlToUint8Array(base64url: string): Uint8Array {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}
